import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { useInstrumentResolver } from "../../../hooks/markets/useInstrumentResolver";
import { TradeHistoryItem } from "../../../interfaces/history";
import { getPositionEffectParts } from "../../../utils/trade";
import { apiNum } from "../../../utils/math";
import { getTxExplorerUrl } from "../../../utils/chain";
import CopyRow from "../../shared/ViewDetailsModal/CopyRow";
import {
  DetailRow,
  FormattedPrice,
  FormattedTime,
  SignedValue,
  SizeWithUsd,
} from "./DetailRow";
import HistoryDetailsShell from "./HistoryDetailsShell";

interface ITradeHistoryDetailsModalProps {
  trade?: TradeHistoryItem;
  onHide?: () => void;
  style?: CSSProperties;
}

function TradeHistoryDetailsModal({
  trade,
  onHide,
  style,
}: ITradeHistoryDetailsModalProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.details.TradeHistoryDetailsModal",
  });
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();

  const instrumentName = trade
    ? resolveInstrumentName(trade as any) || trade.instrument_name
    : undefined;
  const { amount_precision, price_precision } = trade
    ? resolveMarketPrecision(trade as any)
    : { amount_precision: 2, price_precision: 2 };

  if (!trade) {
    return (
      <HistoryDetailsShell show={false} onHide={onHide} style={style}>
        <span />
      </HistoryDetailsShell>
    );
  }

  const isBuy = trade.side === SideResponse.Buy;
  const sideLabel = isBuy
    ? t("buy", { defaultValue: "Buy" })
    : t("sell", { defaultValue: "Sell" });

  const { base, reason } = getPositionEffectParts(
    trade.position_effect,
    trade.exit_reason,
    t
  );
  // e.g. "close long (Liquidated)" — the row capitalizes it.
  const effect =
    [base, reason && `(${reason})`].filter(Boolean).join(" ") || undefined;

  return (
    <HistoryDetailsShell
      show={!!trade}
      onHide={onHide}
      instrumentName={instrumentName}
      copyId={{
        label: t("trade_id", { defaultValue: "Trade ID" }),
        value: trade.trade_id,
      }}
      style={style}
    >
      <DetailRow
        label={t("side", { defaultValue: "Side" })}
        value={
          <span
            style={{
              color: isBuy ? COLORS.up.one : COLORS.down.one,
            }}
          >
            {sideLabel}
          </span>
        }
      />
      <DetailRow
        label={t("type", { defaultValue: "Type" })}
        value={effect}
      />
      <DetailRow
        label={t("size", { defaultValue: "Size" })}
        value={
          <SizeWithUsd
            amount={trade.amount}
            price={trade.price}
            amountPrecision={amount_precision}
            pricePrecision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("price", { defaultValue: "Price" })}
        value={
          <FormattedPrice value={trade.price} precision={price_precision} />
        }
      />
      <DetailRow
        label={t("index_price", { defaultValue: "Index Price" })}
        value={
          <FormattedPrice
            value={trade.index_price}
            precision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("fees", { defaultValue: "Fees" })}
        value={
          // Null (no verified fee schedule) is "-"; SignedValue rendered an
          // empty cell for it, since the element itself is never null.
          apiNum(trade.total_fees as any) === undefined ? (
            "-"
          ) : (
            <SignedValue
              value={trade.total_fees}
              precision={price_precision}
              invert
            />
          )
        }
      />
      <DetailRow
        label={t("order_id", { defaultValue: "Order ID" })}
        value={trade.order_id}
      />
      <DetailRow
        label={t("date_time", { defaultValue: "Date & Time" })}
        value={<FormattedTime value={trade.created_timestamp} unit="nanos" />}
      />
      <CopyRow
        label={t("transaction", { defaultValue: "Transaction" })}
        value={trade.tx_hash}
        // Explorer base URL comes from the chain config, never hardcoded.
        href={trade.tx_hash ? getTxExplorerUrl(trade.tx_hash) : undefined}
        hrefLabel={t("view_tx_on_explorer", {
          defaultValue: "View transaction on block explorer",
        })}
      />
    </HistoryDetailsShell>
  );
}

export default TradeHistoryDetailsModal;
